import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, Users, Building2, Package, 
  FolderOpen, Settings, LogOut, Menu, X, ChevronDown, Lock, Files
} from 'lucide-react';
import { Toaster } from 'sonner';

// i18n
import { LanguageProvider, useLanguage } from './i18n';
import LanguageSelector from './components/LanguageSelector';

// Pages
import QdLogin from './pages/QdLogin';
import QdDashboard from './pages/QdDashboard';
import QdQuotes from './pages/QdQuotes';
import QdQuoteEditor from './pages/QdQuoteEditor';
import QdClients from './pages/QdClients';
import QdCatalog from './pages/QdCatalog';
import QdDocuments from './pages/QdDocuments';
import QdAccounts from './pages/QdAccounts';
import QdTemplates from './pages/QdTemplates';
import ChangePassword from './ChangePassword';

// Auth check
const isAuthenticated = () => {
  const token = localStorage.getItem('qd_token');
  const user = localStorage.getItem('qd_user');
  return token && user;
};

const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem('qd_user') || '{}');
  } catch {
    return {};
  }
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  if (!isAuthenticated()) {
    return <Navigate to="/quote/login" replace />;
  }
  
  if (allowedRoles) {
    const user = getUser();
    if (!allowedRoles.includes(user.role)) {
      return <Navigate to="/quote" replace />;
    }
  }
  
  return children;
};

// Sidebar Navigation
const Sidebar = ({ isOpen, setIsOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const { t } = useLanguage();
  
  const handleLogout = () => {
    localStorage.removeItem('qd_token');
    localStorage.removeItem('qd_user');
    navigate('/quote/login');
  };
  
  const navItems = [
    { path: '/quote', icon: LayoutDashboard, labelKey: 'nav.dashboard', roles: ['user', 'admin', 'superadmin'] },
    { path: '/quote/quotes', icon: FileText, labelKey: 'nav.quotes', roles: ['user', 'admin', 'superadmin'] },
    { path: '/quote/templates', icon: Files, labelKey: 'nav.templates', roles: ['user', 'admin', 'superadmin'] },
    { path: '/quote/clients', icon: Building2, labelKey: 'nav.clients', roles: ['user', 'admin', 'superadmin'] },
    { path: '/quote/catalog', icon: Package, labelKey: 'nav.catalog', roles: ['user', 'admin', 'superadmin'] },
    { path: '/quote/documents', icon: FolderOpen, labelKey: 'nav.documents', roles: ['admin', 'superadmin'] },
    { path: '/quote/accounts', icon: Users, labelKey: 'nav.accounts', roles: ['superadmin'] },
  ];
  
  const filteredNav = navItems.filter(item => item.roles.includes(user.role));
  
  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-[#14233F] text-white z-50
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center justify-between">
              <Link to="/quote" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#0F7D70] rounded-lg flex items-center justify-center font-bold">
                  K
                </div>
                <span className="font-semibold">Knygomatai Devis</span>
              </Link>
              <button 
                onClick={() => setIsOpen(false)}
                className="lg:hidden p-1 hover:bg-white/10 rounded"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {filteredNav.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || 
                (item.path !== '/quote' && location.pathname.startsWith(item.path));
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                    ${isActive 
                      ? 'bg-[#0F7D70] text-white' 
                      : 'text-gray-300 hover:bg-white/10 hover:text-white'}
                  `}
                >
                  <Icon size={20} />
                  <span>{t(item.labelKey)}</span>
                </Link>
              );
            })}
          </nav>
          
          {/* Language selector */}
          <div className="px-4 py-2 border-t border-white/10">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">{t('common.language')}</span>
              <LanguageSelector variant="minimal" />
            </div>
          </div>
          
          {/* User section */}
          <div className="p-4 border-t border-white/10">
            <div className="mb-3">
              <p className="text-sm font-medium truncate">{user.name}</p>
              <p className="text-xs text-gray-400 truncate">{user.email}</p>
              <span className="inline-block mt-1 px-2 py-0.5 bg-white/10 rounded text-xs capitalize">
                {user.role}
              </span>
            </div>
            
            <button
              onClick={() => setShowPasswordModal(true)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/10 rounded-lg transition-colors mb-2"
            >
              <Lock size={16} />
              <span>{t('nav.changePassword')}</span>
            </button>
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
            >
              <LogOut size={16} />
              <span>{t('nav.logout')}</span>
            </button>
          </div>
        </div>
      </aside>
      
      {/* Password Modal */}
      {showPasswordModal && (
        <ChangePassword onClose={() => setShowPasswordModal(false)} />
      )}
    </>
  );
};

// Main Layout
const QdLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Persist sidebar state
  useEffect(() => {
    const saved = localStorage.getItem('qd_sidebar_open');
    if (saved !== null && window.innerWidth >= 1024) {
      setSidebarOpen(saved === 'true');
    }
  }, []);
  
  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      <div className="flex-1 flex flex-col min-h-screen lg:ml-0">
        {/* Mobile header */}
        <header className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <Menu size={24} />
          </button>
          <span className="font-semibold text-[#14233F]">Knygomatai Devis</span>
          <div className="w-10" />
        </header>
        
        {/* Main content */}
        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

// Main Quote App
const QdApp = () => {
  return (
    <LanguageProvider>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="login" element={<QdLogin />} />
        
        <Route path="/" element={
          <ProtectedRoute>
            <QdLayout><QdDashboard /></QdLayout>
          </ProtectedRoute>
        } />
        
        <Route path="quotes" element={
          <ProtectedRoute>
            <QdLayout><QdQuotes /></QdLayout>
          </ProtectedRoute>
        } />
        
        <Route path="quotes/new" element={
          <ProtectedRoute>
            <QdLayout><QdQuoteEditor /></QdLayout>
          </ProtectedRoute>
        } />
        
        <Route path="quotes/:id" element={
          <ProtectedRoute>
            <QdLayout><QdQuoteEditor /></QdLayout>
          </ProtectedRoute>
        } />
        
        <Route path="templates" element={
          <ProtectedRoute>
            <QdLayout><QdTemplates /></QdLayout>
          </ProtectedRoute>
        } />
        
        <Route path="clients" element={
          <ProtectedRoute>
            <QdLayout><QdClients /></QdLayout>
          </ProtectedRoute>
        } />
        
        <Route path="catalog" element={
          <ProtectedRoute>
            <QdLayout><QdCatalog /></QdLayout>
          </ProtectedRoute>
        } />
        
        <Route path="documents" element={
          <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
            <QdLayout><QdDocuments /></QdLayout>
          </ProtectedRoute>
        } />
        
        <Route path="accounts" element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <QdLayout><QdAccounts /></QdLayout>
          </ProtectedRoute>
        } />
        
        <Route path="*" element={<Navigate to="/quote" replace />} />
      </Routes>
    </LanguageProvider>
  );
};

export default QdApp;
